import { GridCell, Wall } from '../types';

interface AStarNode {
  row: number;
  col: number;
  g: number;
  h: number;
  f: number;
  parent: AStarNode | null;
}

/**
 * A* pathfinding on a bitflag-based maze grid.
 * Returns an array of GridCell waypoints from start to end (inclusive).
 * Returns empty array if no path exists.
 */
export function findPath(
  cells: number[][],
  height: number,
  width: number,
  start: GridCell,
  end: GridCell,
): GridCell[] {
  if (start.row === end.row && start.col === end.col) {
    return [{ row: start.row, col: start.col }];
  }

  const openSet: AStarNode[] = [];
  const closedSet = new Set<string>();

  const heuristic = (row: number, col: number): number =>
    Math.abs(row - end.row) + Math.abs(col - end.col);

  const startNode: AStarNode = {
    row: start.row,
    col: start.col,
    g: 0,
    h: heuristic(start.row, start.col),
    f: heuristic(start.row, start.col),
    parent: null,
  };
  openSet.push(startNode);

  const neighbors: { wall: number; dr: number; dc: number }[] = [
    { wall: Wall.NORTH, dr: -1, dc: 0 },
    { wall: Wall.EAST, dr: 0, dc: 1 },
    { wall: Wall.SOUTH, dr: 1, dc: 0 },
    { wall: Wall.WEST, dr: 0, dc: -1 },
  ];

  while (openSet.length > 0) {
    // Find node with lowest f score
    let lowestIdx = 0;
    for (let i = 1; i < openSet.length; i++) {
      if (openSet[i].f < openSet[lowestIdx].f) {
        lowestIdx = i;
      }
    }
    const current = openSet[lowestIdx];

    if (current.row === end.row && current.col === end.col) {
      return reconstructPath(current);
    }

    openSet.splice(lowestIdx, 1);
    const key = `${current.row},${current.col}`;
    closedSet.add(key);

    for (const { wall, dr, dc } of neighbors) {
      // Check if wall blocks movement
      if ((cells[current.row][current.col] & wall) !== 0) continue;

      const nr = current.row + dr;
      const nc = current.col + dc;
      if (nr < 0 || nr >= height || nc < 0 || nc >= width) continue;

      const nKey = `${nr},${nc}`;
      if (closedSet.has(nKey)) continue;

      const g = current.g + 1;
      const h = heuristic(nr, nc);

      const existing = openSet.find(n => n.row === nr && n.col === nc);
      if (existing) {
        if (g < existing.g) {
          existing.g = g;
          existing.f = g + h;
          existing.parent = current;
        }
      } else {
        openSet.push({ row: nr, col: nc, g, h, f: g + h, parent: current });
      }
    }
  }

  return []; // No path found
}

function reconstructPath(node: AStarNode): GridCell[] {
  const path: GridCell[] = [];
  let current: AStarNode | null = node;
  while (current) {
    path.unshift({ row: current.row, col: current.col });
    current = current.parent;
  }
  return path;
}
